import { useId } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useAnalyticsConsent } from "@/hooks/useAnalyticsConsent";
import { COOKIE_POLICY_URL, PRIVACY_POLICY_URL } from "@/lib/consent";
import { cn } from "@/lib/utils";

const LINK =
  "font-medium text-k-accent-ink underline underline-offset-2 hover:text-k-ink-hi";

/**
 * Settings > Privacy: the user's analytics consent, changeable at any time
 * (ADR-051 §5). Turning it off records the refusal; `lib/otel-consent.ts`
 * then shuts the SDK down and deletes the session id, with no reload.
 * "No choice yet" renders as off — analytics only run on an explicit yes.
 */
export function AnalyticsSettings() {
  const { consent, setConsent } = useAnalyticsConsent();
  const labelId = useId();
  const descriptionId = useId();
  const enabled = consent === "granted";

  return (
    <Card data-testid="analytics-settings">
      <CardContent className="flex items-start justify-between gap-6 py-5">
        <div className="min-w-0 space-y-1">
          <h3 id={labelId} className="font-semibold">
            Analytics
          </h3>
          <p id={descriptionId} className="text-sm text-muted-foreground">
            Optional performance and usage telemetry from this browser (page
            loads, requests, clicks, device and network details) that helps us
            measure the dashboard and support you. Off unless you turn it on.
            See our{" "}
            <a
              href={PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={LINK}
            >
              Privacy Policy
            </a>{" "}
            and{" "}
            <a
              href={COOKIE_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={LINK}
            >
              Cookie Policy
            </a>
            .
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-labelledby={labelId}
          aria-describedby={descriptionId}
          onClick={() => setConsent(!enabled)}
          className={cn(
            "relative mt-0.5 inline-flex h-6 w-11 shrink-0 items-center rounded-full border border-transparent",
            "transition-colors duration-160 ease-k-out",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-k-accent",
            enabled ? "bg-k-accent" : "bg-k-line-2",
          )}
        >
          <span
            aria-hidden="true"
            className={cn(
              "pointer-events-none block h-5 w-5 rounded-full bg-white shadow-k-sm",
              "transition-transform duration-160 ease-k-out",
              enabled ? "translate-x-[21px]" : "translate-x-[1px]",
            )}
          />
        </button>
      </CardContent>
    </Card>
  );
}
